import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { OrderSchemaType, ItemPath } from '../../../../order.schema';
import { FormGroup, Label } from '../../../../style';
import {
  DimensionField,
  DimensionValueInput,
  ToleranceInput,
  TolerancePair,
  ToleranceRow,
  ToleranceRowDivider,
  ToleranceSeparator,
  ToleranceSign,
} from './FinalProductDimensionsSection.style';

// Inclui `length`, que não é dimensão de formato mas tem o mesmo par de tolerância.
type ToleranceColumn = 'diameter' | 'thickness' | 'width' | 'length';

interface DimensionToleranceInputProps {
  itemPath: ItemPath;
  column: ToleranceColumn;
  label: string;
}

const toFormNumber = (raw: string): number | undefined => (raw === '' ? undefined : Number(raw));

// Tolerância guarda magnitude; o sinal é da coluna (Minus/Plus).
const toMagnitude = (raw: string): number | undefined => {
  if (raw === '') return undefined;
  const num = Number(raw);
  return Number.isFinite(num) ? Math.abs(num) : undefined;
};

/**
 * Dimensão com tolerância no mesmo campo: valor à esquerda e o par + / − empilhado à direita.
 * Nasce vazio; o placeholder −0 / +3 é só sugestão.
 *
 * Os três inputs levam `inline-input`: sem ela o bloco de globalStyle.ts impõe
 * height 38px, fundo e borda a todo input[type=number] e estoura o campo.
 */
const DimensionToleranceInput: React.FC<DimensionToleranceInputProps> = ({ itemPath, column, label }) => {
  const { control, setValue } = useFormContext<OrderSchemaType>();

  const valuePath = `${itemPath}.${column}`;
  const plusPath = `${itemPath}.${column}TolerancePlus`;
  const minusPath = `${itemPath}.${column}ToleranceMinus`;

  const value = useWatch({ control, name: valuePath as any }) as number | string | undefined;
  const plus = useWatch({ control, name: plusPath as any }) as number | string | undefined;
  const minus = useWatch({ control, name: minusPath as any }) as number | string | undefined;

  const write = (path: string, next: number | undefined) => {
    setValue(path as any, next, { shouldDirty: true, shouldValidate: false });
  };

  return (
    <FormGroup>
      <Label htmlFor={valuePath}>{label}</Label>
      <DimensionField>
        <DimensionValueInput
          id={valuePath}
          className="inline-input"
          autoComplete="off"
          type="number"
          step="any"
          value={(value as any) ?? ''}
          onChange={(e) => write(valuePath, toFormNumber(e.target.value))}
          placeholder="Digite"
        />
        <ToleranceSeparator />
        <TolerancePair title="Tolerância (mm): para cima e para baixo">
          <ToleranceRow>
            <ToleranceSign aria-hidden="true">+</ToleranceSign>
            <ToleranceInput
              id={plusPath}
              className="inline-input"
              autoComplete="off"
              type="number"
              step="any"
              min="0"
              aria-label={`${label}: tolerância para cima (mm)`}
              value={(plus as any) ?? ''}
              onChange={(e) => write(plusPath, toMagnitude(e.target.value))}
              placeholder="3"
            />
          </ToleranceRow>
          <ToleranceRowDivider />
          <ToleranceRow>
            <ToleranceSign aria-hidden="true">−</ToleranceSign>
            <ToleranceInput
              id={minusPath}
              className="inline-input"
              autoComplete="off"
              type="number"
              step="any"
              min="0"
              aria-label={`${label}: tolerância para baixo (mm)`}
              value={(minus as any) ?? ''}
              onChange={(e) => write(minusPath, toMagnitude(e.target.value))}
              placeholder="0"
            />
          </ToleranceRow>
        </TolerancePair>
      </DimensionField>
    </FormGroup>
  );
};

export default DimensionToleranceInput;
